/**
 * OnboardingsListView Component
 *
 * Main list view for onboardings and folders.
 * R9: migrated to the canonical SDataTable (folder recipe, playbook
 * 13-DataTables.mdx) with the Gmail-style header-checkbox selection entry
 * (`checkboxReveal`) and the standard batch bar (Delete selected + Move to
 * folder — replaces the old Bulk Delete button). Every pre-migration testid
 * is preserved: `onboarding-row-{id}`, `folder-icon`, `onboarding-icon`,
 * `onboarding-actions-{id}`, `onboarding-search-input`, `menu-*`,
 * `create-folder-btn`, `create-onboarding-btn`, and the table's
 * `aria-label="onboardings table"`.
 *
 * Sprint 7.1 - Table layout; R9 - canonical table migration
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Folder as FolderIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  CreateNewFolder as CreateNewFolderIcon,
  MoreVert as MoreIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CloneIcon,
  DriveFileMove as MoveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  SDataTable,
  useSelectionMode,
  SelectionActionsBar,
  HeaderSearchField,
  type SDataTableColumnDef,
} from '@modules/ui-components/dialogs/SDataTable';
import { useOnboardings } from '../hooks/useOnboardings';
import { useFolders } from '../hooks/useFolders';
import { useOnboardingActionsV2 } from '../hooks/useOnboardingActionsV2';
import { OnboardingListItem } from '../types/onboardings.types';
import { MoveToFolderDialog } from './MoveToFolderDialog';
import { ONBOARDING_SETTINGS_TEST_IDS } from '../testIds';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';

const ONBOARDING_LIST_CREATE_ANCHOR = GUIDE_ANCHORS.onboardingListCreate;

/** Icon-only header buttons share the primary button's 44px height. */
const HEADER_ICON_BUTTON_PX = 44;
/** Title row bottom margin (theme spacing): −6px, see the header comment. */
const ONBOARDINGS_HEADER_ROW_MB = -0.75;

/**
 * Props for OnboardingsListView
 */
export interface OnboardingsListViewProps {
  /** Project ID */
  projectId: string;
  /** Current user ID */
  userId: string;
  /** Current folder ID (null = root) */
  currentFolderId: string | null;
  /** Current folder name (for breadcrumb) */
  currentFolderName?: string;
  /** Callback when folder is clicked */
  onFolderClick: (folderId: string) => void;
  /** Callback when onboarding is clicked */
  onOnboardingClick: (onboardingId: string) => void;
  /** Callback to navigate to root */
  onNavigateToRoot?: () => void;
  /** Callback to open create onboarding dialog */
  onCreateOnboarding?: () => void;
  /** Callback to open create folder dialog */
  onCreateFolder?: () => void;
  /** Opens the "Onboarding settings" drawer (gear in the header); no gear when absent. */
  onOpenSettings?: () => void;
  /** Callback after folder/onboarding list changes */
  onListChanged?: () => void;
  /** Callback when empty state changes */
  onEmptyStateChange?: (isEmpty: boolean) => void;
  /** Callback when edit onboarding is clicked */
  onEditOnboarding?: (onboarding: OnboardingListItem) => void;
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

type MoveTarget =
  | { kind: 'single'; item: OnboardingListItem }
  | { kind: 'bulk' };

/**
 * OnboardingsListView Component
 */
export function OnboardingsListView({
  projectId,
  userId,
  currentFolderId,
  currentFolderName,
  onFolderClick,
  onOnboardingClick,
  onNavigateToRoot,
  onCreateOnboarding,
  onCreateFolder,
  onOpenSettings,
  onListChanged,
  onEmptyStateChange,
  onEditOnboarding,
}: OnboardingsListViewProps) {
  const guideService = useMemo(() => new GuideService(), []);

  // Hooks
  const { items, loading, error, hasMore, loadMore, refresh } = useOnboardings({
    projectId,
    folderId: currentFolderId,
  });

  const { folders, deleteFolder, renameFolder, getFolderContentsCount } = useFolders({ projectId });

  const { cloneOnboarding, deleteOnboarding, moveToFolder } = useOnboardingActionsV2({
    projectId,
    userId,
  });

  // Gmail entry (checkboxReveal): mode is ALWAYS on — the header checkbox is
  // the selection entry, the batch bar shows whenever something is selected.
  const selection = useSelectionMode({ defaultMode: true });

  // Selection is scoped to the open folder — clear on navigation.
  useEffect(() => {
    selection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId]);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<OnboardingListItem | null>(null);

  // Delete confirmation dialog state (single item via kebab)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [folderContentsCount, setFolderContentsCount] = useState(0);
  const [itemToDelete, setItemToDelete] = useState<OnboardingListItem | null>(null);

  // Bulk delete confirmation (batch bar)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Move to folder dialog state (single via kebab OR bulk via batch bar)
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);

  // Clone action state
  const [cloneLoading, setCloneLoading] = useState(false);
  const [itemBeingCloned, setItemBeingCloned] = useState<OnboardingListItem | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Notify parent about empty state (only at root level)
  useEffect(() => {
    if (!loading && currentFolderId === null) {
      const isEmpty = items.length === 0 && folders.length === 0;
      onEmptyStateChange?.(isEmpty);
    }
  }, [items.length, folders.length, loading, currentFolderId, onEmptyStateChange]);

  // Menu handlers
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, item: OnboardingListItem) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedItem(item);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setSelectedItem(null);
  }, []);

  // Action handlers
  const handleOpenInNewTab = useCallback(() => {
    if (selectedItem && selectedItem.type === 'onboarding') {
      window.open(`/project/${projectId}/onboarding-v2/${selectedItem.id}`, '_blank');
    }
    handleMenuClose();
  }, [selectedItem, projectId, handleMenuClose]);

  const handleClone = useCallback(async () => {
    const itemToClone = selectedItem?.type === 'onboarding' ? selectedItem : null;
    handleMenuClose();

    if (!itemToClone || cloneLoading) return;

    setCloneError(null);
    setItemBeingCloned(itemToClone);
    setCloneLoading(true);

    try {
      const newName = `${itemToClone.name} (Copy)`;
      await cloneOnboarding(itemToClone.id, newName);
      await refresh();
      onListChanged?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to clone onboarding';
      setCloneError(message);
      console.error('Clone failed:', err);
    } finally {
      setCloneLoading(false);
      setItemBeingCloned(null);
    }
  }, [selectedItem, cloneLoading, cloneOnboarding, refresh, handleMenuClose, onListChanged]);

  const handleMoveToFolder = useCallback(() => {
    if (selectedItem && selectedItem.type === 'onboarding') {
      setMoveTarget({ kind: 'single', item: selectedItem });
    }
    handleMenuClose();
  }, [selectedItem, handleMenuClose]);

  const handleMoveConfirm = useCallback(async (folderId: string | null) => {
    if (!moveTarget) return;
    const ids = moveTarget.kind === 'single'
      ? [moveTarget.item.id]
      : selection.selectedIds;

    await Promise.all(ids.map((id) => moveToFolder(id, folderId)));
    await refresh();
    onListChanged?.();
    setMoveTarget(null);
    selection.clearSelection();
  }, [moveTarget, moveToFolder, refresh, onListChanged, selection]);

  const handleMoveCancel = useCallback(() => {
    setMoveTarget(null);
  }, []);

  const handleRename = useCallback(() => {
    // TODO: Open rename dialog for folders
    console.log('Rename:', selectedItem?.id);
    handleMenuClose();
  }, [selectedItem, handleMenuClose]);

  // Edit onboarding handler
  const handleEditOnboarding = useCallback(() => {
    if (selectedItem && selectedItem.type === 'onboarding') {
      onEditOnboarding?.(selectedItem);
    }
    handleMenuClose();
  }, [selectedItem, onEditOnboarding, handleMenuClose]);

  // Delete confirmation
  const handleDeleteClick = useCallback(async () => {
    if (!selectedItem) return;

    // Store the item to delete before closing menu
    setItemToDelete(selectedItem);

    // For folders, check contents count
    if (selectedItem.type === 'folder') {
      const count = await getFolderContentsCount(selectedItem.id);
      setFolderContentsCount(count);
    } else {
      setFolderContentsCount(0);
    }

    setDeleteDialogOpen(true);
    // Close menu but don't clear selectedItem yet
    setMenuAnchor(null);
  }, [selectedItem, getFolderContentsCount]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    try {
      if (itemToDelete.type === 'onboarding') {
        await deleteOnboarding(itemToDelete.id);
      } else {
        await deleteFolder(itemToDelete.id);
      }
      await refresh();
      onListChanged?.();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setSelectedItem(null);
    }
  }, [itemToDelete, deleteOnboarding, deleteFolder, refresh, onListChanged]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setSelectedItem(null);
  }, []);

  // Bulk delete (batch bar) — same deleteOnboarding per id (versions cascade)
  const handleBulkDeleteConfirm = useCallback(async () => {
    setBulkBusy(true);
    try {
      await Promise.all(selection.selectedIds.map((id) => deleteOnboarding(id)));
      await refresh();
      onListChanged?.();
      setBulkDeleteOpen(false);
      selection.clearSelection();
    } catch (err) {
      console.error('Bulk delete failed:', err);
    } finally {
      setBulkBusy(false);
    }
  }, [deleteOnboarding, onListChanged, refresh, selection]);

  // Row click handler
  const handleRowClick = useCallback((item: OnboardingListItem) => {
    if (item.type === 'folder') {
      onFolderClick(item.id);
    } else {
      onOnboardingClick(item.id);
    }
  }, [onFolderClick, onOnboardingClick]);

  // ------------------------------------------------------------------
  // Table definition (canonical SDataTable — folder recipe)
  // ------------------------------------------------------------------
  const columns = useMemo<SDataTableColumnDef<OnboardingListItem>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      // Name takes the spare width; Locales / Updated keep their size (C-15).
      grow: true,
      // Search matches folder names AND onboarding names (pre-migration parity).
      accessorFn: (item) => item.name,
      filterFn: 'contains',
      Header: ({ column }) => (
        <HeaderSearchField
          label="Name"
          value={(column.getFilterValue() as string) ?? ''}
          onChange={(next) => column.setFilterValue(next === '' ? undefined : next)}
          testId="onboarding-search-input"
        />
      ),
      Cell: ({ row }) => {
        const item = row.original;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {item.type === 'folder' ? (
              <FolderIcon data-testid="folder-icon" color="primary" />
            ) : (
              <DescriptionIcon data-testid="onboarding-icon" />
            )}
            <Typography>{item.name}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'locales',
      header: 'Locales',
      // Locales + Updated are sized to their content (not MRT's 180px
      // default) so the table fits its container down to 900px (AC-035).
      size: 140,
      Cell: ({ row }) => {
        const item = row.original;
        if (item.type !== 'onboarding' || !item.languages || item.languages.length === 0) {
          return null;
        }
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {item.languages.map((lang) => (
              <Chip
                key={lang}
                label={lang}
                size="small"
                variant={item.defaultLanguage === lang ? 'filled' : 'outlined'}
                sx={{ height: 20, typography: 'caption', '& .MuiChip-label': { px: 0.75 } }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      id: 'updated',
      header: 'Updated',
      size: 120,
      Cell: ({ row }) => <>{formatDate(row.original.updatedAt)}</>,
    },
  ], []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header - always visible. Secondary actions are icon-only buttons
          with tooltips; Create Onboarding is the only contained primary.
          The negative bottom margin pulls the reserved bulk slot up by the
          buttons' overhang below the title ((44px row − 32px h1) / 2 = 6px),
          so the bulk buttons sit 4px under the title and the table starts
          ~37px under it — with or without a selection (no layout shift). */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: ONBOARDINGS_HEADER_ROW_MB,
        }}
      >
        <Typography variant="h5" component="h1" noWrap>
          Onboardings
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {onOpenSettings && (
            <Tooltip title="Onboarding settings">
              <IconButton
                color="primary"
                onClick={onOpenSettings}
                aria-label="Onboarding settings"
                data-testid={ONBOARDING_SETTINGS_TEST_IDS.settingsBtn}
                sx={{ width: HEADER_ICON_BUTTON_PX, height: HEADER_ICON_BUTTON_PX }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Create folder">
            <IconButton
              color="primary"
              onClick={onCreateFolder}
              aria-label="Create folder"
              data-testid="create-folder-btn"
              sx={{ width: HEADER_ICON_BUTTON_PX, height: HEADER_ICON_BUTTON_PX }}
            >
              <CreateNewFolderIcon />
            </IconButton>
          </Tooltip>
          <WithGuideHelp
            guideKey={ONBOARDING_LIST_CREATE_ANCHOR.guideKey}
            guideService={guideService}
            data-testid={ONBOARDING_LIST_CREATE_ANCHOR.testId}
            position="top-right"
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateOnboarding}
              data-testid="create-onboarding-btn"
              sx={{
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'shimmer 3s ease-in-out infinite',
                },
                '@keyframes shimmer': {
                  '0%': { left: '-100%' },
                  '50%': { left: '100%' },
                  '100%': { left: '100%' },
                },
              }}
            >
              Create Onboarding
            </Button>
          </WithGuideHelp>
        </Box>
      </Box>

      {/* Batch actions — fixed-height slot between title and table: the bar
          appears inside it without shifting the table (no layout jump). */}
      <SelectionActionsBar
        selection={selection}
        reserveSpace
        onDeleteSelected={() => setBulkDeleteOpen(true)}
        busy={bulkBusy}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<MoveIcon fontSize="small" />}
          disabled={bulkBusy}
          onClick={() => setMoveTarget({ kind: 'bulk' })}
          data-testid="onb-bulk-move-button"
        >
          Move to folder
        </Button>
      </SelectionActionsBar>

      {/* Breadcrumb (when in folder) */}
      {currentFolderId && currentFolderName && (
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              component="button"
              underline="hover"
              color="inherit"
              onClick={onNavigateToRoot}
              sx={{ cursor: 'pointer' }}
            >
              Root
            </Link>
            <Typography color="text.primary">{currentFolderName}</Typography>
          </Breadcrumbs>
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {cloneError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCloneError(null)}>
          {cloneError}
        </Alert>
      )}

      {/* Initial loading state — page-owned spinner, NOT MRT skeletons
          (state.isLoading renders skeleton rows with empty row.original —
          feedback_mrt_isloading_skeleton_rows). */}
      {loading && items.length === 0 && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state - only show when not loading and no error */}
      {!loading && !error && items.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No onboardings yet
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateOnboarding}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: 'shimmer 3s ease-in-out infinite',
              },
              '@keyframes shimmer': {
                '0%': { left: '-100%' },
                '50%': { left: '100%' },
                '100%': { left: '100%' },
              },
            }}
          >
            Create Onboarding
          </Button>
        </Box>
      )}

      {/* Table */}
      {items.length > 0 && (
        <SDataTable<OnboardingListItem>
          columns={columns}
          data={items}
          getRowId={(item) => item.id}
          // Folders-first comes from useOnboardings construction — MRT column
          // sorting would interleave folders with onboardings.
          enableSorting={false}
          enableTopToolbar={false}
          enableRowActions
          enablePagination
          initialState={{ pagination: { pageIndex: 0, pageSize: 50 } }}
          // Gmail entry: header checkbox always visible, row checkboxes
          // hover-revealed; folders excluded per-row; Esc clears; row click
          // keeps navigating.
          checkboxReveal
          enableColumnFilters
          columnFilterDisplayMode="custom"
          enableRowSelection={(row) => row.original?.type === 'onboarding'}
          state={{ rowSelection: selection.rowSelection }}
          onRowSelectionChange={selection.setRowSelection}
          // aria-label is load-bearing: test-kit + jest resolve the table by it.
          muiTableProps={{ size: 'small', 'aria-label': 'onboardings table' }}
          muiTableBodyRowProps={({ row }) => ({
            hover: true,
            'data-testid': `onboarding-row-${row.original.id}`,
            onClick: () => handleRowClick(row.original),
            sx: { cursor: 'pointer' },
          })}
          // One actions button per row, exactly as before the migration —
          // the page-owned Menu below stays the single actions surface.
          renderRowActions={({ row }) => (
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, row.original)}
              aria-label="actions"
              data-testid={`onboarding-actions-${row.original.id}`}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          )}
          renderEmptyRowsFallback={({ table }) => {
            const query = table
              .getState()
              .columnFilters?.find((f) => f.id === 'name')?.value as string | undefined;
            return (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {query
                    ? <>No onboardings matching &quot;{query}&quot;</>
                    : 'No onboardings yet'}
                </Typography>
              </Box>
            );
          }}
        />
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedItem?.type === 'onboarding' && (
          <MenuItem onClick={handleOpenInNewTab} data-testid="menu-open-new-tab">
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open in new tab</ListItemText>
          </MenuItem>
        )}
        {selectedItem?.type === 'onboarding' && (
          <MenuItem onClick={handleClone} disabled={cloneLoading} data-testid="menu-clone">
            <ListItemIcon>
              <CloneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Clone</ListItemText>
          </MenuItem>
        )}
        {selectedItem?.type === 'onboarding' && (
          <MenuItem onClick={handleMoveToFolder} data-testid="menu-move-to-folder">
            <ListItemIcon>
              <MoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move to folder</ListItemText>
          </MenuItem>
        )}
        {selectedItem?.type === 'onboarding' && (
          <MenuItem onClick={handleEditOnboarding} data-testid="menu-edit">
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {selectedItem?.type === 'folder' && (
          <MenuItem onClick={handleRename} data-testid="menu-rename">
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }} data-testid="menu-delete">
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        {/* Destructive confirmation — no header X; footer Cancel/Delete
            is the only decision surface. Title centred. */}
        <DialogTitle
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            gap: 1,
            pb: 1,
          }}
        >
          <Box />
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Confirm Delete
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {itemToDelete?.type === 'folder' ? (
            folderContentsCount > 0 ? (
              <Typography>
                This folder contains <strong>{folderContentsCount}</strong> item(s).
                Please move or delete the contents before deleting this folder.
              </Typography>
            ) : (
              <Typography>
                Are you sure you want to delete the folder "{itemToDelete?.name}"?
                This action cannot be undone.
              </Typography>
            )
          ) : (
            <Typography>
              Are you sure you want to delete "{itemToDelete?.name}"?
              This will also delete all versions. This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading || (itemToDelete?.type === 'folder' && folderContentsCount > 0)}
          >
            {deleteLoading ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation (batch bar) */}
      <Dialog
        open={bulkDeleteOpen}
        onClose={() => { if (!bulkBusy) setBulkDeleteOpen(false); }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            gap: 1,
            pb: 1,
          }}
        >
          <Box />
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Confirm Delete
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography data-testid="onb-bulk-delete-message">
            Are you sure you want to delete <strong>{selection.selectedIds.length}</strong> selected
            onboarding{selection.selectedIds.length === 1 ? '' : 's'}?
            This will also delete all their versions. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkDeleteConfirm}
            color="error"
            variant="contained"
            disabled={bulkBusy || selection.selectedIds.length === 0}
            data-testid="onb-bulk-delete-confirm"
          >
            {bulkBusy ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load more (for lazy loading) */}
      {hasMore && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={loadMore}>Load More</Button>
        </Box>
      )}

      {/* Loading more indicator */}
      {loading && items.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Move to Folder Dialog */}
      <MoveToFolderDialog
        open={Boolean(moveTarget)}
        onClose={handleMoveCancel}
        onMove={handleMoveConfirm}
        folders={folders}
        currentFolderId={currentFolderId}
        itemName={moveTarget?.kind === 'single'
          ? moveTarget.item.name
          : `${selection.selectedIds.length} onboardings`}
      />

      <Dialog
        open={cloneLoading}
        aria-labelledby="clone-loading-title"
        slotProps={{
          paper: {
            sx: { minWidth: 320 },
          },
        }}
      >
        <Box
          role="status"
          aria-live="polite"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.25,
            px: 3,
            py: 3,
          }}
        >
          <CircularProgress />
          <Typography id="clone-loading-title" variant="subtitle1" sx={{ fontWeight: 600 }}>
            Cloning onboarding...
          </Typography>
          {itemBeingCloned && (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, textAlign: 'center' }}>
              {itemBeingCloned.name}
            </Typography>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

export default OnboardingsListView;
